import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { notification, Steps, Typography } from 'antd'
import { loadStripe } from '@stripe/stripe-js'
import { useHistory } from 'react-router-dom'

import CustomModal from '../CustomModal'
import Step1 from './components/Step1'
import Step2 from './components/Step2'
import Step3 from './components/Step3'
import { bookCall, getPaymentMethods } from '../../../context/subscription'
import { useAuthContext } from '../../../context/auth'
import { CallData, TExpert } from '../../../context/types'
import { currentBrand, stripePKey } from '../../../config'
import { customLocalStorage } from '../../../context/customStorage'
import { useExpertsContext } from '../../../context/experts'
import { useChannelContext } from '../../../context/channel'
import useSubscriptions from './UseSubscriptions'

const { Title } = Typography
import './style.css'
import CallPrice from '../CallPrice/CallPrice'

const isMobile = window.innerWidth < 576

interface BookingModalProps {
	visible: boolean
	onCancel: () => void
	expert: TExpert
}

const BookingModal: FunctionComponent<BookingModalProps> = (props) => {
	const { visible, expert, onCancel } = props
	const { auth } = useAuthContext()

	return (
		<CustomModal destroyOnClose visible={visible} onCancel={onCancel} width={auth.token ? 950 : 600}>
			<RequestCallModal expert={expert} onCancel={onCancel} />
		</CustomModal>
	)
}

export default BookingModal

// ------------------------------------------------------------------------------------------------

export type HandleBookParam = {
	subscriptionTypeId: number
	overageTypeId?: number
	subscriptionId?: number
	paymentMethodId?: string
	coupon?: string
	phoneCountryId?: string
	phoneCountryCode?: string
	phone?: number
	quantity?: number
	metadata?: { [key: string]: any }
}

interface RequestCallModalProps {
	expert: TExpert
	onCancel: () => void
}

const { Step } = Steps

const stripePromise = loadStripe(stripePKey)
const initialCallData = { reason: '', start_time: '', files: [] } as CallData

const RequestCallModal: FunctionComponent<RequestCallModalProps> = (props) => {
	const { expert, onCancel } = props
	const { auth, actions } = useAuthContext()
	const {
		channel: { selectedChannel },
	} = useChannelContext()
	const { experts, actions: expertActions } = useExpertsContext()
	const { subscriptions, callLengthList, total } = useSubscriptions(auth, experts.expert || expert)
	const { push } = useHistory()

	const [step, setStep] = useState(0)
	const [cards, setCards] = useState([])
	const [duration, setDuration] = useState<number>(null)
	const [callData, setCallData] = useState<CallData>({ ...initialCallData, expert })

	useEffect(() => {
		if (auth?.token) {
			getPaymentMethods()
				.then((r) => setCards(r?.paymentMethods || []))
				.catch(handleError)
		} else {
			setCards([])
		}
	}, [auth, expert])

	useEffect(() => {
		if (!expert.profile?.brand) {
			expertActions.getExpertBySlug(expert.slug)
			return () => {
				expertActions.resetExpert()
			}
		}
	}, [])

	useEffect(() => {
		if (!duration) {
			setDuration(callLengthList?.[0])
		}
	}, [callLengthList])

	const filteredSubscriptions = useMemo(() => {
		return subscriptions.filter((v) => v.call_length === duration)
	}, [duration, subscriptions])

	const handleSchedule = (startTime: string) => {
		setCallData({ ...callData, start_time: startTime })
		setStep(1)
	}

	const handleChangeReason = (reason: string, files: any[]) => {
		setCallData({ ...callData, reason, files })
		setStep(2)
	}

	const handleError = (error?: Error) => {
		notification.error({ message: 'Error', description: error.message || 'Oops! Something went wrong!' })
	}

	const handleBook = (params: HandleBookParam) => {
		const {
			subscriptionId,
			overageTypeId,
			subscriptionTypeId,
			paymentMethodId,
			coupon,
			phoneCountryId,
			phoneCountryCode,
			phone,
			quantity,
			metadata,
		} = params
		return bookCall({
			reason: callData.reason,
			start_time: callData.start_time,
			expert_id: expert.id,
			subscription_type_id: subscriptionTypeId,
			subscription_id: subscriptionId,
			overage_type_id: overageTypeId,
			payment_method_id: paymentMethodId,
			coupon,
			phone_country_id: phoneCountryId,
			phone_country_code: phoneCountryCode,
			phone,
			quantity,
			metadata,
		})
			.then((data) => {
				if (data?.call?.user) {
					actions.setUserFields(data?.call?.user)
				}
				customLocalStorage.removeItem('coupon')
				notification.success({ message: 'Success', description: 'Call booked' })

				setCallData({ ...initialCallData, expert })
				setStep(0)
				onCancel()
				push({ pathname: '/profile/calls', search: '?thank_you' })
			})
			.catch(handleError)
	}

	const onSetStep = (newStep: number) => () => setStep(newStep)

	return (
		<div className="RequestCallModal">
			{!!auth.token && (
				<div className="RequestCallModalSteps">
					<Steps current={step} direction={isMobile ? 'vertical' : 'horizontal'}>
						<Step title="Pick a time slot" description="See the availability of your expert" />
						<Step title="Scheduling info" description="Write your goal, comments, add files" />
						<Step title="Payment" description="Choose the payment option" />
					</Steps>
					<Title level={4} style={{ margin: '20px 0 0' }}>
						Booking with: {expert.profile.user.first_name} {expert.profile.user.last_name}
						{currentBrand.showCallPrice && !selectedChannel?.integration_config.hide_call_pricing && (
							<>
								{' '}
								- <CallPrice payRate={expert.profile.pay_rate} />{' '}
							</>
						)}
					</Title>
				</div>
			)}

			{step === 0 && (
				<Step1
					expert={expert}
					duration={duration}
					callLengthList={callLengthList}
					onAction={handleSchedule}
					onChangeDuration={setDuration}
				/>
			)}

			{step === 1 && <Step2 onBack={onSetStep(0)} onAction={handleChangeReason} />}

			{step === 2 && (
				<Elements stripe={stripePromise}>
					<Step3
						onAction={handleBook}
						onBack={onSetStep(1)}
						cards={cards}
						subscriptions={filteredSubscriptions}
						call={callData}
						total={total}
					/>
				</Elements>
			)}
		</div>
	)
}
